/**
 * Helper utilities for generating and parsing academic journal URLs
 * Target format: /journal/vol{volNumber}/iss{issNumber}/{articleId}/
 * Example: /journal/vol20/iss3/5/
 */

/**
 * Extracts a numeric or alphanumeric identifier for volume
 * e.g., "Vol-20" -> "vol20", "20" -> "vol20", "Vol 1" -> "vol1"
 */
export const formatVolParam = (volume) => {
  if (!volume && volume !== 0) return 'vol1';
  const str = String(volume).trim();
  // Extract number if present
  const match = str.match(/\d+/);
  if (match) {
    return `vol${match[0]}`;
  }
  // Fallback to lowercase sanitized string
  const sanitized = str.toLowerCase().replace(/[^a-z0-9]/g, '');
  return sanitized.startsWith('vol') ? sanitized : `vol${sanitized || '1'}`;
};

/**
 * Extracts a numeric or alphanumeric identifier for issue
 * e.g., "Issue 3" -> "iss3", "3" -> "iss3", "Issue-1" -> "iss1"
 */
export const formatIssParam = (issue) => {
  if (!issue && issue !== 0) return 'iss1';
  const str = String(issue).trim();
  // Extract number if present
  const match = str.match(/\d+/);
  if (match) {
    return `iss${match[0]}`;
  }
  // Fallback to lowercase sanitized string
  const sanitized = str.toLowerCase().replace(/[^a-z0-9]/g, '');
  return sanitized.startsWith('iss') ? sanitized : `iss${sanitized || '1'}`;
};

/**
 * Parses URL parameter back to pure number or clean string
 * e.g., "vol20" -> "20", "iss3" -> "3"
 */
export const parseParamNumber = (param) => {
  if (!param) return '';
  const match = String(param).match(/\d+/);
  return match ? match[0] : String(param).replace(/^(vol|iss)/i, '');
};

/**
 * Generates the dedicated article page URL
 * @param {object} article - The article object
 * @param {number|string} [index] - Optional fallback index/number if article has no articleNumber
 * @returns {string} - e.g. "/journal/vol20/iss3/5/"
 */
export const getArticlePageUrl = (article, index) => {
  if (!article) return '/';

  const vol = formatVolParam(article.volume || article.Vol || article.vol);
  const iss = formatIssParam(article.issue || article.Issue || article.iss);

  // Preferred identifier: articleNumber > sequential index (1, 2, 3...) > id
  let articleId = article.articleNumber;
  if (!articleId && index !== undefined && index !== null) {
    articleId = String(index + 1);
  }
  if (!articleId && article.id) {
    articleId = String(article.id);
  }
  if (!articleId && article.title) {
    articleId = article.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 40);
  }
  if (!articleId) {
    articleId = '1';
  }

  return `/journal/${vol}/${iss}/${articleId}/`;
};

/**
 * Checks if a given article matches the route parameters
 */
export const matchesArticleParams = (article, volParam, issParam, articleIdParam, index) => {
  if (!article) return false;

  const targetVolNum = parseParamNumber(volParam);
  const targetIssNum = parseParamNumber(issParam);

  const artVol = article.volume || article.Vol || article.vol || '';
  const artIss = article.issue || article.Issue || article.iss || '';

  const artVolNum = parseParamNumber(artVol);
  const artIssNum = parseParamNumber(artIss);

  // Check volume and issue matching (lenient if either is empty)
  const volMatches = !targetVolNum || !artVolNum || targetVolNum === artVolNum;
  const issMatches = !targetIssNum || !artIssNum || targetIssNum === artIssNum;

  if (!volMatches || !issMatches) return false;

  const cleanArticleIdParam = String(articleIdParam).toLowerCase().trim();

  // Match by id directly
  if (article.id && String(article.id).toLowerCase() === cleanArticleIdParam) {
    return true;
  }

  // Match by articleNumber
  if (article.articleNumber && String(article.articleNumber) === cleanArticleIdParam) {
    return true;
  }

  // Match by index (e.g. "5" -> index 4)
  if (index !== undefined && String(index + 1) === cleanArticleIdParam) {
    return true;
  }

  // Match by slugified title
  if (article.title) {
    const slug = article.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 40);
    if (slug === cleanArticleIdParam) {
      return true;
    }
  }

  return false;
};
